import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar/index';
import { ReloadOutlined } from '@ant-design/icons';
import { Button, Divider, Card, Tabs } from 'antd';
import { checkPermission } from '@/utils/permission';
import { CustomerServiceStatus } from '@/utils/helpers';
import { getListCustomer } from '@/redux/actions/partnerCustomerActions';
import BaseRangeFilter from '@/components/BaseRangeFilter';
import DynamicTable from '@/components/DynamicTable';
import useUrlFilters from '@/hooks/useUrlFilters';
import FilterableMultiSelect from '@/components/FilterableMultiSelect';
import { buildCustomerColumns } from '@/columns/buildCustomerColumns';
import { fetchFilters } from '@/redux/actions/filterActions';

const { TabPane } = Tabs;

const title = 'Khách hàng';
const path = 'partner-customers';
const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: `/${path}`, breadcrumbName: title },
];


export default function PartnerCustomerList() {

    const location = useLocation();
    const dispatch = useDispatch();

    const items = useSelector(state => state.partnerCustomerReducer.list.items);
    const pagination = useSelector(state => state.partnerCustomerReducer.list.pagination);
    const authUser = useSelector(state => state.auth.authUser);
    const filterData = useSelector(state => state.filterReducer.list);

    const customer_source_active_list = filterData?.sources;
    const branchActiveList = filterData?.branches;
    const customerStatusActiveList = filterData?.statuses;

    useEffect(() => {
        dispatch(fetchFilters());
    }, [dispatch]);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const routeName = 'customer';
    const allowCreate = checkPermission(routeName, 'create');
    const allowDelete = checkPermission(routeName, 'delete');
    const allowDetail = checkPermission(routeName, 'detail');

    const query = useMemo(() => {
        return qs.parse(location.search.slice(1));
    }, [location.search]);

    const loadData = useCallback((q) => {
        setIsLoading(true);
        dispatch(getListCustomer(q)).finally(() => setIsLoading(false));
    }, [dispatch]);

    useEffect(() => {
        loadData(query);
    }, [query, loadData]);

    const { handleFilterChange } = useUrlFilters({
        initialExtra: { page: 1, per_page: 30 }, // tuỳ chọn
        rangeMap: {
            // Ngày tạo
            created_date: { from: 'created_date_from', to: 'created_date_to', type: 'date' },
            // Lịch hẹn
            appointment_schedule: { from: 'appointment_schedule_from', to: 'appointment_schedule_to', type: 'date' },
            // Ngày chốt
            usage_date: { from: 'usage_date_from', to: 'usage_date_to', type: 'date' },
            // Ngày sinh
            birthday: { from: 'birthday_from', to: 'birthday_to', type: 'month-day' },
        }
    });


    const columns = useMemo(() => buildCustomerColumns(
        {
            allowDetail: allowDetail,
            detailLink: true,
            role: authUser?.role_code
        },
        {
            // ẨN/HIỆN cột:
            hiddenKeys: ['actions', 'telesale', 'telesale_status']
        }
    ), []);

    //console.log('items', items);

    const tables = <DynamicTable
        dataSource={items || []}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        moduleKey="partner_customers"
    />

    return (
        <div>
            <PageTitle routes={routes} title={title} />
            <TableActionBar
                isShowAddButton={allowCreate}
                isShowDeleteButton={allowDelete}
                disabled={!selectedRowKeys.length}
                showSearch
                showFilter={true}
                onSearch={value => handleFilterChange('keyword', value)}
                defaultKeyword={query.keyword}
                filterColSpan={8}
                activeFilter={
                    query.appointment_schedule_from ||
                    query.customer_status_id ||
                    query.branch_id ||
                    query.customer_source_id ||
                    query.usage_date_from
                }
                filters={
                    [
                        <FilterableMultiSelect
                            options={branchActiveList || []}
                            placeholder="Chọn cơ sở"
                            parameterName='branch_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <BaseRangeFilter
                            name="appointment_schedule"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Lịch hẹn', 'Đến ngày']}
                        />,
                        <BaseRangeFilter
                            name="usage_date"
                            query={query}
                            onChangeFilter={handleFilterChange}
                            placeholder={['Ngày chốt', 'Đến ngày']}
                        />,
                        <FilterableMultiSelect
                            options={CustomerServiceStatus || []}
                            placeholder="Trạng thái dịch vụ chốt"
                            parameterName='service_status'
                            optionValue="value"
                            optionLabel="label"
                        />,
                        <FilterableMultiSelect
                            options={customerStatusActiveList || []}
                            placeholder="Trạng thái khách hàng"
                            parameterName='customer_status_id'
                            optionValue="id"
                            optionLabel="name"
                        />,
                        <FilterableMultiSelect
                            options={customer_source_active_list || []}
                            placeholder="Nguồn khách hàng"
                            parameterName='customer_source_id'
                            optionValue="id"
                            optionLabel="name"
                        />
                    ]
                }
            >
                <Button icon={<ReloadOutlined />} loading={isLoading} onClick={() => loadData(query)}>Refresh</Button>
                {selectedRowKeys.length > 0 && <span><Divider type="vertical" />Đang chọn {selectedRowKeys.length} bản ghi </span>}
            </TableActionBar>

            <Card size='small' bodyStyle={{ padding: 0 }} className='has-custom-column-table-btn'>
                <Tabs
                    onChange={(value) => handleFilterChange('tab', value)}
                    defaultActiveKey={query.tab}
                    type="card"
                    size="small"
                    tabBarStyle={{ fontWeight: 600 }}
                >
                    <TabPane tab="Toàn bộ khách hàng" key="all">
                        {tables}
                    </TabPane>
                    <TabPane tab="Cho số điện thoại" key="have_phone">
                        {tables}
                    </TabPane>
                    <TabPane tab="Đặt được lịch hẹn" key={3}>
                        {tables}
                    </TabPane>
                    <TabPane tab="Hẹn không đến" key={4}>
                        {tables}
                    </TabPane>
                    <TabPane tab="Đã chốt" key={10}>
                        {tables}
                    </TabPane>
                    <TabPane tab="Chưa chốt" key={9}>
                        {tables}
                    </TabPane>
                    <TabPane tab="Khách gặp vấn đề" key="complaint">
                        {tables}
                    </TabPane>
                </Tabs>
            </Card>
        </div>
    );
}
